import moment from 'moment';
import React from "react";

const formatDurationSeconds = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
};

const getModeIconClass = (mode) => {
    const modeMap = {
        'CAR': 'drive',
        'WALK': 'walk',
        'BUS': 'bus',
        'RAIL': 'rail',
        'TRAM': 'tram',
        'SUBWAY': 'subway',
        'METRO': 'metro'
    };
    
    return modeMap[mode?.toUpperCase()] || mode?.toLowerCase() || 'walk';
};

const formatDepartureDelay = (seconds) => {
    const totalMinutes = Math.floor(seconds / 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) {
        return `${hours}hr ${minutes}m`;
    }
    return `${minutes} mins`;
};

const getStatusText = (status) => {
    switch (status) {
        case 'early':
            return 'Early';
        case 'cancelled':
            return 'Cancelled';
        case 'ontime':
            return 'On time';
        case 'minor':
        case 'major':
        case 'severe':
            return 'Delayed';
        default:
            return 'On time';
    }
};

export function NextPlan({
    index,
    total,
    nextPlan,
    isSelected = false,
    onSelect,
    selectedMode = 'RAIL',
}) {
    // Find first leg with a trip and delay information (for delay/status info)
    const legWithTrip = nextPlan.node?.legs?.find(leg =>
        leg.trip?.stoptimesForDate?.[0]?.departureDelay !== undefined
    );

    const lastLegWithTrip = [...(nextPlan.node?.legs || [])].reverse().find(leg =>
        leg.trip?.stoptimesForDate?.[0]?.departureDelay !== undefined
    );

    // Use appropriate start and end times based on mode
    let startTime, endTime;
    
    if (selectedMode === 'RAIL') {
        // For rail mode: use first transit leg departure and last transit leg arrival
        // to match the behavior in JourneySummary
        const firstTransitLeg = nextPlan.node?.legs?.find(leg => leg.trip?.gtfsId);
        const lastTransitLeg = [...(nextPlan.node?.legs || [])].reverse().find(leg => leg.trip?.gtfsId);
        
        startTime = firstTransitLeg ? moment(firstTransitLeg.from?.departure?.scheduledTime) : moment(nextPlan.node?.start);
        endTime = lastTransitLeg ? moment(lastTransitLeg.to?.arrival?.scheduledTime) : moment(nextPlan.node?.end);
    } else {
        // For bus mode: keep using complete journey times
        startTime = moment(nextPlan.node?.start);
        endTime = moment(nextPlan.node?.end);
    }
    
    // Get origin and destination names based on mode
    let originName, destinationName, travelLegs;
    
    if (selectedMode === 'BUS') {
        // For bus mode: use transit leg boundaries for origin/destination
        const transitLegs = nextPlan.node?.legs?.filter(leg => leg.trip?.gtfsId) || [];
        
        if (transitLegs.length > 0) {
            // Use first transit leg origin and last transit leg destination
            const firstTransitLeg = transitLegs[0];
            const lastTransitLeg = transitLegs[transitLegs.length - 1];
            originName = firstTransitLeg?.from?.stop?.name || firstTransitLeg?.from?.name;
            destinationName = lastTransitLeg?.to?.stop?.name || lastTransitLeg?.to?.name;
        } else {
            // No transit legs, use journey start/end
            originName = nextPlan.node?.legs?.[0]?.from?.name || nextPlan.node?.legs?.[0]?.from?.stop?.name;
            destinationName = nextPlan.node?.legs?.[nextPlan.node?.legs?.length - 1]?.to?.name || nextPlan.node?.legs?.[nextPlan.node?.legs?.length - 1]?.to?.stop?.name;
        }
        
        // For bus mode: show all legs including walking
        travelLegs = nextPlan.node?.legs?.filter(leg => leg.mode) || [];
    } else {
        // For rail mode: keep existing behavior
        originName = legWithTrip?.from?.stop?.name;
        destinationName = lastLegWithTrip?.to?.stop?.name;
        travelLegs = nextPlan.node?.legs?.filter(leg => leg.trip?.gtfsId !== undefined) || [];
    }
    
    const departureDelay = legWithTrip?.trip?.stoptimesForDate?.[0]?.departureDelay;

    const estimatedScheduledDeparture = departureDelay && departureDelay > 0 ? moment(legWithTrip?.from.departure.estimated.time) : null;
    const estimatedScheduledArrival = departureDelay && departureDelay > 0 ? moment(lastLegWithTrip?.to.arrival.estimated.time) : null;

    const duration = formatDurationSeconds(nextPlan.node?.duration ?? 0);
    const status = getStatusText(nextPlan.node?.status);

    if (!nextPlan?.node) {
        return null;
    }

    return (
        <>
            <div
                onClick={onSelect}
                className={`transition-all duration-200 cursor-pointer ${status === 'Cancelled' ? 'bg-red-50' : 'bg-white'
                    } ${isSelected ? 
                        (status === 'On time' ? 'bg-blue-100' : 
                         status === 'Delayed' ? 'bg-orange-100' : 
                         status === 'Cancelled' ? 'bg-red-100' : 'bg-blue-100') : ''
                    } ${typeof index === 'undefined' || typeof total === 'undefined' || total === 1 ? "rounded-lg" :
                        index === 0 ? "rounded-t-lg" : index === (total - 1)
                    }`}>
                <div className="flex">
                    {/* Status indicator bar on the left */}
                    <div className={`${status === 'On time' ? 'bg-journeyAlertsBlue ' :
                        status === 'Delayed' ? 'bg-orange-500' :
                            status === 'Cancelled' ? 'bg-red-600' : 'bg-journeyAlertsBlue'
                        } w-4`} />

                        <div className="flex flex-col w-full px-3 sm:px-4 lg:px-6 py-2 sm:py-3 lg:py-4">
                        {/* Route title */}
                        <div className="flex gap-1 justify-between">
                            <div>
                                <p className="text-base sm:text-lg lg:text-xl font-bold">
                                    {originName}
                                    <span className="text-[#808080] font-coHeadline"> to </span>
                                </p>
                                <p className="text-base sm:text-lg lg:text-xl font-bold" style={{ lineHeight: '1' }}>
                                    {destinationName}
                                </p>
                            </div>

                            <div className="flex flex-col">
                                {status === 'Delayed' && (
                                    <>
                                        {estimatedScheduledDeparture && estimatedScheduledArrival && (
                                            <p className={`text-base sm:text-lg lg:text-xl font-bold whitespace-nowrap`}>{estimatedScheduledDeparture.format('HH:mm')} - {estimatedScheduledArrival?.format('HH:mm')}</p>
                                        )}
                                    </>
                                )}
                                {startTime && (
                                    <p className={`text-base sm:text-lg lg:text-xl font-bold whitespace-nowrap ${(status === 'Cancelled' || status === 'Delayed') && 'text-gray-500 line-through decoration-red-500 decoration-2'}`}>{startTime.format('HH:mm')} - {endTime.format('HH:mm')}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex gap-2 mb-2 sm:mb-3 lg:mb-4 justify-between">
                            {/* travel icons */}
                            {travelLegs?.length && travelLegs?.length >= 1 && (
                                <div className="flex">
                                    {travelLegs.map((leg, index) => (
                                        <div key={index}>
                                            <i className={`text-2xl sm:text-3xl lg:text-4xl zip-icon-mode_${getModeIconClass(leg?.mode)}`}></i>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className={`flex gap-1 ${!travelLegs?.length ? 'ml-auto' : ''}`}>
                                {status === 'Cancelled' ? <p className="text-base sm:text-lg lg:text-xl">Cancelled</p> :
                                    <>
                                        <p className="text-gray-500 text-base sm:text-lg lg:text-xl">{duration}</p>
                                        {(departureDelay ?? 0) > 0 && <p className="text-gray-500 text-base sm:text-lg lg:text-xl">({formatDepartureDelay(departureDelay)} late)</p>}
                                        {(departureDelay ?? 0) < 0 && <p className="text-gray-500 text-base sm:text-lg lg:text-xl">({formatDepartureDelay(Math.abs(departureDelay))} early)</p>}
                                    </>
                                }
                            </div>
                        </div>
                    </div>
                </div>

                {index !== undefined && total !== undefined && index !== (total - 1) &&
                    <hr className="border-gray-200" />
                }
            </div>
        </>
    );
} 